import { useState, useEffect } from 'react';
import axios from 'axios';
import AIAssistant from './AIAssistant';

const API = 'https://weridesudan.com/api/admin';

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token);
    } catch { setError('Invalid credentials'); }
  };
  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginBox}>
        <h1 style={styles.logo}>🚗 We Ride Sudan</h1>
        <h2 style={styles.loginTitle}>Admin Dashboard</h2>
        <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={styles.input} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.button} onClick={handleLogin}>Login</button>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState('rides');
  const [rides, setRides] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/rides`, { headers }).then(r => setRides(r.data.rides || []));
    axios.get(`${API}/users`, { headers }).then(r => setUsers(r.data.users || []));
    axios.get(`${API}/stats`, { headers }).then(r => setStats(r.data));
  }, []);

  const drivers = users.filter(u => u.role === 'driver');
  const riders = users.filter(u => u.role === 'rider');

  const toggleUser = async (id: string, isActive: boolean) => {
    await axios.patch(`${API}/users/${id}`, { isActive: !isActive }, { headers });
    const res = await axios.get(`${API}/users`, { headers });
    setUsers(res.data.users || []);
  };

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>🚗 WeRide Admin</h2>
        {['rides','drivers','riders','stats','ai'].map(t => (
          <div key={t} style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}) }} onClick={() => setTab(t)}>
            {t === 'rides' && '🚕 Rides'}
            {t === 'drivers' && '🧑 Drivers'}
            {t === 'riders' && '👤 Riders'}
            {t === 'stats' && '📊 Statistics'}
            {t === 'ai' && '🤖 AI Assistant'}
          </div>
        ))}
        <div style={styles.navItem} onClick={onLogout}>🚪 Logout</div>
      </div>
      <div style={styles.main}>
        {tab === 'ai' && <AIAssistant token={token} />}
        {tab === 'stats' && stats && (
          <div>
            <h2>📊 Overview</h2>
            <div style={styles.statsGrid}>
              {[['Total Rides', stats.totalRides],['Active Rides', stats.activeRides],['Total Drivers', stats.totalDrivers],['Total Riders', stats.totalRiders],['Total Revenue', `${stats.totalRevenue} SDG`]].map(([label, value]) => (
                <div key={label} style={styles.statCard}><div style={{ fontSize: 32, fontWeight: 'bold' }}>{value}</div><div>{label}</div></div>
              ))}
            </div>
          </div>
        )}
        {tab === 'rides' && (
          <div>
            <h2>🚕 All Rides</h2>
            <table style={styles.table}>
              <thead><tr><th>Rider</th><th>Driver</th><th>Pickup</th><th>Dropoff</th><th>Fare</th><th>Status</th></tr></thead>
              <tbody>{rides.map(r => (
                <tr key={r._id}>
                  <td>{r.rider?.name || 'N/A'}</td>
                  <td>{r.driver?.name || 'Unassigned'}</td>
                  <td>{r.pickup?.address || '-'}</td>
                  <td>{r.destination?.address || '-'}</td>
                  <td>{r.fare?.estimated} SDG</td>
                  <td><span style={{ ...styles.badge, backgroundColor: r.status === 'completed' ? '#4caf50' : r.status === 'accepted' ? '#2196f3' : '#ff9800' }}>{r.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {tab === 'drivers' && (
          <div>
            <h2>🧑 Drivers</h2>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Status</th><th>Rating</th><th>Action</th></tr></thead>
              <tbody>{drivers.map(d => (
                <tr key={d._id}>
                  <td>{d.name}</td><td>{d.phone}</td>
                  <td>{d.isOnline ? '🟢 Online' : '🔴 Offline'}</td>
                  <td>⭐ {d.rating || 5}</td>
                  <td><button style={{ ...styles.actionBtn, backgroundColor: d.isActive ? '#e53935' : '#43a047' }} onClick={() => toggleUser(d._id, d.isActive)}>{d.isActive ? 'Suspend' : 'Activate'}</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {tab === 'riders' && (
          <div>
            <h2>👤 Riders</h2>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Total Rides</th><th>Action</th></tr></thead>
              <tbody>{riders.map(r => (
                <tr key={r._id}>
                  <td>{r.name}</td><td>{r.phone}</td><td>{r.email}</td><td>{r.totalRides || 0}</td>
                  <td><button style={{ ...styles.actionBtn, backgroundColor: r.isActive ? '#e53935' : '#43a047' }} onClick={() => toggleUser(r._id, r.isActive)}>{r.isActive ? 'Suspend' : 'Activate'}</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const handleLogin = (t: string) => { localStorage.setItem('admin_token', t); setToken(t); };
  const handleLogout = () => { localStorage.removeItem('admin_token'); setToken(''); };
  return token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />;
}

const styles: any = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' },
  loginBox: { backgroundColor: '#fff', padding: 40, borderRadius: 12, width: 380, textAlign: 'center' as const },
  logo: { fontSize: 24, marginBottom: 8 },
  loginTitle: { color: '#333', marginBottom: 24 },
  input: { width: '100%', padding: 12, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' as const },
  button: { width: '100%', padding: 14, backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  error: { color: 'red', marginBottom: 12 },
  container: { display: 'flex', height: '100vh', fontFamily: 'sans-serif' },
  sidebar: { width: 220, backgroundColor: '#1a1a2e', color: '#fff', padding: 20 },
  sidebarTitle: { fontSize: 16, marginBottom: 24 },
  navItem: { padding: '12px 16px', cursor: 'pointer', borderRadius: 8, marginBottom: 8, textTransform: 'capitalize' as const },
  navActive: { backgroundColor: '#1a73e8' },
  main: { flex: 1, padding: 30, backgroundColor: '#f5f5f5', overflowY: 'auto' as const },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 20 },
  statCard: { backgroundColor: '#fff', padding: 24, borderRadius: 12, textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  badge: { padding: '4px 10px', borderRadius: 20, color: '#fff', fontSize: 12 },
  actionBtn: { padding: '6px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer' },
};
